/**
 * Enlaces del correo que llevan a una ACCIÓN dentro de la plataforma (Arca-Team/product#3).
 *
 * El botón del correo apunta al enlace corto de comparsa (`/m/:token`), que reenvía a
 * `/token?token=…` con los `utm_*` y, validados, `action`, `amount` (entero 1-1000) y `plan`
 * (el `user_epp_config.id` del plan, un UUID). Aquí se recogen esos tres.
 *
 * La captura es GENÉRICA: se guarda `{ action, amount, plan }` sin decidir nada. Quien lo
 * enruta es el `Dashboard`, que solo conoce `increase_contribution` (abrir el modal de
 * aportación). Una acción que no conoce se ignora y el usuario se queda donde estaba. Otro
 * enlace (p. ej. «Invita y gana» a su sección) solo tiene que añadir su caso allí.
 *
 * 🔴 Por qué se captura en `App` y no donde se usa: sin sesión, `PrivateRoute` redirige con
 * un `<Navigate>` cuyo efecto borra la query antes de que monte nada de detrás del login, y
 * `LoginFormV2` navega a `/dashboard/home` sin query. Así que `App` llama a
 * `captureMagicLinkFromUrl` en el inicializador perezoso de un `useState` (síncrono, en el
 * primer render, antes de cualquier efecto de react-router): lo guarda en `localStorage`
 * con caducidad de 30 minutos y limpia la URL. El `Dashboard` lo consume una sola vez.
 *
 * La caducidad permite el login manual (token caducado), el cambio de contraseña del primer
 * acceso y «He olvidado mi contraseña» (chirigota#630): el enlace de recuperación se abre en
 * OTRA pestaña, y por eso va en `localStorage`, que comparten todas, y no en `sessionStorage`.
 * 30 minutos dan para ir al correo, cambiar la contraseña y volver.
 */
import { useState } from 'react';

export const MAGIC_LINK_ACTIONS = {
	INCREASE_CONTRIBUTION: 'increase_contribution'
} as const;

export const MAGIC_LINK_PRESET_STORAGE_KEY = 'arca.magicLink.preset';
/** Un enlace viejo no debe abrir nada días después. */
export const MAGIC_LINK_PRESET_TTL_MS = 30 * 60 * 1000;

/** Lo que trae el enlace. `amount` y `plan` solo si son válidos. */
export interface MagicLinkPreset {
	action: string;
	/** Euros, entero de 1 a 1000 (las mismas reglas que el enlace corto de comparsa). */
	amount?: number;
	/** `user_epp_config.id` del plan, en minúscula. */
	plan?: string;
}

interface StoredMagicLinkPreset {
	preset: MagicLinkPreset;
	expiresAt: number;
}

/** Las claves que se llevan de la URL al guardarlas: `token` y `utm_*` se quedan. */
const MAGIC_LINK_PARAMS = ['action', 'amount', 'plan'];

const ACTION_PATTERN = /^[a-z_]{1,64}$/;
const AMOUNT_PATTERN = /^\d{1,4}$/;
const MAX_AMOUNT = 1000;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const parseAmount = (raw: string | null): number | undefined => {
	if (raw === null || !AMOUNT_PATTERN.test(raw)) return undefined;
	const amount = Number(raw);
	return amount >= 1 && amount <= MAX_AMOUNT ? amount : undefined;
};

const parsePlan = (raw: string | null): string | undefined =>
	raw !== null && UUID_PATTERN.test(raw) ? raw.toLowerCase() : undefined;

export const parseMagicLinkPreset = (params: URLSearchParams): MagicLinkPreset | null => {
	const action = params.get('action');
	if (action === null || !ACTION_PATTERN.test(action)) return null;

	const amount = parseAmount(params.get('amount'));
	const plan = parsePlan(params.get('plan'));
	return {
		action,
		...(amount !== undefined ? { amount } : {}),
		...(plan !== undefined ? { plan } : {})
	};
};

/**
 * Lee el enlace de la URL, lo guarda y lo quita de la URL (una recarga no lo vuelve a leer).
 * Sin enlace no toca nada: ni la URL ni lo que hubiera guardado.
 */
export const captureMagicLinkFromUrl = (): MagicLinkPreset | null => {
	const params = new URLSearchParams(window.location.search);
	const preset = parseMagicLinkPreset(params);
	if (!preset) return null;

	try {
		const stored: StoredMagicLinkPreset = {
			preset,
			expiresAt: Date.now() + MAGIC_LINK_PRESET_TTL_MS
		};
		localStorage.setItem(MAGIC_LINK_PRESET_STORAGE_KEY, JSON.stringify(stored));
	} catch {
		// Sin localStorage (modo privado estricto) se pierde el enlace, no la página.
	}

	MAGIC_LINK_PARAMS.forEach((key) => params.delete(key));
	const query = params.toString();
	window.history.replaceState(
		window.history.state,
		'',
		`${window.location.pathname}${query ? `?${query}` : ''}`
	);
	return preset;
};

const isStoredPreset = (value: unknown): value is StoredMagicLinkPreset => {
	const candidate = value as StoredMagicLinkPreset | null;
	return typeof candidate?.expiresAt === 'number' && typeof candidate?.preset?.action === 'string';
};

/** Devuelve lo guardado si no ha caducado y lo borra siempre: se usa una sola vez. */
export const consumeMagicLinkPreset = (): MagicLinkPreset | null => {
	try {
		const raw = localStorage.getItem(MAGIC_LINK_PRESET_STORAGE_KEY);
		localStorage.removeItem(MAGIC_LINK_PRESET_STORAGE_KEY);
		if (raw === null) return null;
		const stored: unknown = JSON.parse(raw);
		if (!isStoredPreset(stored) || Date.now() >= stored.expiresAt) return null;
		return stored.preset;
	} catch {
		return null;
	}
};

/**
 * El enlace pendiente, consumido UNA vez al montar (inicializador perezoso, como
 * `consumeTokenLoginIntent` en el Dashboard). Una recarga ya no lo encuentra.
 */
export const useMagicLinkContribution = (): MagicLinkPreset | null => {
	const [preset] = useState(consumeMagicLinkPreset);
	return preset;
};
